import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Wallet, Percent, ShieldCheck, ChevronRight, Check,
  AlertTriangle, ExternalLink, Loader2, ArrowRight, Zap
} from 'lucide-react';

const STEPS = [
  { id: 'wallet',      label: 'Add Wallet',           icon: Wallet },
  { id: 'tolerance',   label: 'Payment Tolerance',    icon: Percent },
  { id: 'verification',label: 'Payment Verification', icon: ShieldCheck },
];

const VERIFICATION_OPTIONS = [
  {
    value: 'exact_amount',
    label: 'Exact Amount',
    desc: 'Only accept payments that exactly match the invoice amount. Requires TXID for verification.',
  },
  {
    value: 'round_up',
    label: 'Round Up',
    desc: 'Accept payments that are equal to or above the invoice amount (overpayments accepted).',
  },
  {
    value: 'round_down',
    label: 'Round Down',
    desc: 'Accept payments that are within the tolerance below the invoice amount.',
  },
];

export default function OnboardingWizard({ onComplete }) {
  const { updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);

  // Step 1 — Wallet
  const [networks, setNetworks] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [walletForm, setWalletForm] = useState({ address: '', label: '', network_id: '', enabled_tokens: [] });
  const [walletSaving, setWalletSaving] = useState(false);
  const [walletAdded, setWalletAdded] = useState(false);

  // Step 2 — Tolerance
  const [tolerance, setTolerance] = useState({ enabled: false, percentage: 1.0 });
  const [toleranceSaving, setToleranceSaving] = useState(false);

  // Step 3 — Verification
  const [verification, setVerification] = useState({ method: 'exact_amount' });
  const [verificationSaving, setVerificationSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [netRes, tokRes] = await Promise.all([
          axios.get(`${API}/networks`),
          axios.get(`${API}/networks/all-tokens`),
        ]);
        const nets = netRes.data.filter(n => n.is_active !== false);
        const toks = tokRes.data.filter(t => t.is_active !== false);
        setNetworks(nets);
        setTokens(toks);
        if (nets[0]) {
          const defaultTokens = toks.filter(t => t.network_id === nets[0].id).map(t => t.id);
          setWalletForm(f => ({ ...f, network_id: nets[0].id, enabled_tokens: defaultTokens }));
        }
      } catch { /* network might not be configured yet */ }
    };
    fetchData();
  }, []);

  const getNetworkTokens = (nid) => tokens.filter(t => t.network_id === nid && t.is_active !== false);

  const handleNetworkChange = (nid) => {
    const nTokens = getNetworkTokens(nid).map(t => t.id);
    setWalletForm(f => ({ ...f, network_id: nid, enabled_tokens: nTokens }));
  };

  const toggleToken = (tid) => {
    setWalletForm(f => {
      const enabled = f.enabled_tokens.includes(tid)
        ? f.enabled_tokens.filter(id => id !== tid)
        : [...f.enabled_tokens, tid];
      return { ...f, enabled_tokens: enabled };
    });
  };

  const handleAddWallet = async () => {
    if (!walletForm.address) { toast.error('Enter a wallet address'); return; }
    if (!walletForm.network_id) { toast.error('Select a network'); return; }
    if (walletForm.enabled_tokens.length === 0) { toast.error('Enable at least one token'); return; }
    setWalletSaving(true);
    try {
      await axios.post(`${API}/wallets`, walletForm);
      setWalletAdded(true);
      toast.success('Wallet added!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add wallet');
    } finally {
      setWalletSaving(false);
    }
  };

  const handleSaveTolerance = async () => {
    setToleranceSaving(true);
    try {
      await axios.put(`${API}/settings/payment-tolerance`, tolerance);
    } catch { /* skip */ }
    finally { setToleranceSaving(false); }
  };

  const handleSaveVerification = async () => {
    setVerificationSaving(true);
    try {
      await axios.put(`${API}/settings/payment-verification`, verification);
    } catch { /* skip */ }
    finally { setVerificationSaving(false); }
  };

  const handleNext = async () => {
    if (step === 1) await handleSaveTolerance();
    if (step === 2) {
      await handleSaveVerification();
      await finishWizard();
      return;
    }
    setStep(s => s + 1);
  };

  const finishWizard = async () => {
    setCompleting(true);
    try {
      await axios.patch(`${API}/auth/complete-onboarding`);
      updateUser({ onboarding_completed: true });
      onComplete();
    } catch {
      toast.error('Could not save settings');
    } finally {
      setCompleting(false);
    }
  };

  const currentStep = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 dark:bg-black/80 backdrop-blur-sm" data-testid="onboarding-wizard">
      <div className="w-full max-w-lg mx-4">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-3">
            <Zap className="h-3.5 w-3.5 text-indigo-400" />
            <span className="text-xs font-medium text-indigo-300">Quick Setup</span>
          </div>
          <h2 className="text-2xl font-bold text-foreground">Welcome to CoinPayUp</h2>
          <p className="text-muted-foreground text-sm mt-1">Let's get your account ready in 3 quick steps</p>
        </div>

        {/* Step pills */}
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  done ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' :
                  active ? 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border border-indigo-500/30' :
                  'bg-muted text-muted-foreground border border-border'
                }`} data-testid={`wizard-step-${i}`}>
                  {done ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                  <span className="hidden sm:inline">{s.label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px ${i < step ? 'bg-emerald-500/30' : 'bg-border'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-[#0f1117] border border-border rounded-2xl overflow-hidden shadow-2xl">
          {/* Step header */}
          <div className="px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                <currentStep.icon className="h-4.5 w-4.5 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {step === 0 && 'Add Your Receiving Wallet'}
                  {step === 1 && 'Payment Tolerance'}
                  {step === 2 && 'Payment Verification Method'}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {step === 0 && 'Receive crypto payments directly to your wallet'}
                  {step === 1 && 'Accept slightly under-paid invoices automatically'}
                  {step === 2 && 'Choose how to handle incoming payment amounts'}
                </p>
              </div>
            </div>
          </div>

          {/* Step body */}
          <div className="px-6 py-5">

            {/* ── Step 1: Add Wallet ── */}
            {step === 0 && (
              <div className="space-y-4">
                {/* Exchange warning */}
                <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-indigo-600/10 border border-indigo-600/30" data-testid="exchange-wallet-warning">
                  <AlertTriangle className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-indigo-600 leading-relaxed">
                    Do not add wallets from exchanges such as Binance, Coinbase, OKX, Bybit, or KuCoin.{' '}
                    <a
                      href="https://coinpayup.com/blogs/why-coinpayup-cannot-verify-payments-sent-from-exchange-wallets"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-indigo-600/50 hover:text-indigo-700 font-medium inline-flex items-center gap-0.5"
                      data-testid="exchange-wallet-link"
                    >
                      Click here for more information
                      <ExternalLink className="h-3 w-3 ml-0.5" />
                    </a>
                  </p>
                </div>

                {walletAdded ? (
                  <div className="flex items-center gap-2.5 p-4 rounded-xl bg-emerald-500/8 border border-emerald-500/20" data-testid="wallet-added-success">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                      <Check className="h-4 w-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-emerald-400">Wallet added successfully!</p>
                      <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">{walletForm.address}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Wallet Address</label>
                      <Input
                        placeholder="0x... or T..."
                        value={walletForm.address}
                        onChange={e => setWalletForm(f => ({ ...f, address: e.target.value }))}
                        className="text-sm font-mono"
                        data-testid="wizard-wallet-address"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Label <span className="text-muted-foreground/60">(optional)</span></label>
                      <Input
                        placeholder="e.g. Main Wallet"
                        value={walletForm.label}
                        onChange={e => setWalletForm(f => ({ ...f, label: e.target.value }))}
                        className="text-sm"
                        data-testid="wizard-wallet-label"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Network</label>
                      <Select value={walletForm.network_id} onValueChange={handleNetworkChange}>
                        <SelectTrigger className="text-sm" data-testid="wizard-network-select">
                          <SelectValue placeholder="Select network" />
                        </SelectTrigger>
                        <SelectContent>
                          {networks.map(n => (
                            <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {walletForm.network_id && getNetworkTokens(walletForm.network_id).length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-2 block">Accept Tokens</label>
                        <div className="flex flex-wrap gap-2" data-testid="wizard-token-list">
                          {getNetworkTokens(walletForm.network_id).map(t => (
                            <label key={t.id} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs transition-colors ${
                              walletForm.enabled_tokens.includes(t.id)
                                ? 'bg-primary/10 border-primary/30 text-primary'
                                : 'bg-muted/50 border-border text-muted-foreground hover:border-border/80'
                            }`}>
                              <Checkbox
                                checked={walletForm.enabled_tokens.includes(t.id)}
                                onCheckedChange={() => toggleToken(t.id)}
                                className="h-3 w-3"
                              />
                              {t.symbol}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleAddWallet}
                      disabled={walletSaving}
                      className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm"
                      data-testid="wizard-add-wallet-btn"
                    >
                      {walletSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wallet className="h-4 w-4 mr-1.5" />Add Wallet</>}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ── Step 2: Payment Tolerance ── */}
            {step === 1 && (
              <div className="space-y-4" data-testid="wizard-tolerance-step">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Some networks and wallets deduct small fees, so the received amount may be slightly less than the invoice amount. Enable tolerance to automatically accept such payments.
                </p>

                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/40 border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Accept under-payments within tolerance</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {tolerance.enabled ? `Accepting payments within ${tolerance.percentage}% of invoice` : 'Disabled — exact amount required'}
                    </p>
                  </div>
                  <Switch
                    checked={tolerance.enabled}
                    onCheckedChange={v => setTolerance(t => ({ ...t, enabled: v }))}
                    data-testid="wizard-tolerance-toggle"
                  />
                </div>

                {tolerance.enabled && (
                  <div className="space-y-2" data-testid="wizard-tolerance-input-section">
                    <label className="text-xs font-medium text-muted-foreground block">Tolerance Percentage</label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min={0.1}
                        max={10}
                        step={0.1}
                        value={tolerance.percentage}
                        onChange={e => setTolerance(t => ({ ...t, percentage: parseFloat(e.target.value) || 1 }))}
                        className="w-24 text-sm"
                        data-testid="wizard-tolerance-input"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                      <span className="text-xs text-muted-foreground/60">
                        For $100: accept &ge; ${(100 * (1 - (tolerance.percentage || 1) / 100)).toFixed(2)}
                      </span>
                    </div>
                    {tolerance.percentage > 2 && (
                      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/8 border border-amber-500/20 text-xs text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        High tolerance may lead to revenue loss. 1-2% is recommended.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Step 3: Payment Verification ── */}
            {step === 2 && (
              <div className="space-y-3" data-testid="wizard-verification-step">
                {VERIFICATION_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                      verification.method === opt.value
                        ? 'bg-primary/10 border-primary/30'
                        : 'bg-muted/30 border-border hover:border-border/80'
                    }`}
                    data-testid={`wizard-verification-${opt.value}`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      verification.method === opt.value ? 'border-primary bg-primary/20' : 'border-border'
                    }`}>
                      {verification.method === opt.value && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <input
                      type="radio"
                      className="hidden"
                      value={opt.value}
                      checked={verification.method === opt.value}
                      onChange={() => setVerification({ method: opt.value })}
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">{opt.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={step === 2 ? finishWizard : () => setStep(s => s + 1)}
              className="text-muted-foreground hover:text-foreground text-xs"
              data-testid="wizard-skip-btn"
            >
              {step === 2 ? 'Skip & Finish' : 'Skip'}
            </Button>
            <Button
              onClick={handleNext}
              disabled={completing || toleranceSaving || verificationSaving}
              className="ml-auto bg-indigo-600 hover:bg-indigo-500 text-white px-5"
              data-testid="wizard-next-btn"
            >
              {completing || verificationSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : step === 2 ? (
                <><Check className="h-4 w-4 mr-1.5" />Finish Setup</>
              ) : (
                <>Next<ArrowRight className="h-4 w-4 ml-1.5" /></>
              )}
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground/60 mt-4">
          You can change these settings anytime in{' '}
          <span className="text-muted-foreground">Settings</span>
        </p>
      </div>
    </div>
  );
}
